import React, { useState, useEffect, useMemo } from "react";
import { LogEntries } from "../lib/data";

const C = {
  bg: "#070B10",
  surface: "#0D1520",
  surface2: "#111D2E",
  border: "#1A2A40",
  accent: "#00D4FF",
  red: "#FF4D6A",
  green: "#00E5A0",
  yellow: "#FFD166",
  purple: "#A78BFA",
  orange: "#FB923C",
  pink: "#F472B6",
  text: "#E8F0F8",
  muted: "#5A7A99",
  mono: "'JetBrains Mono','Fira Mono',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

const COMPOUND_COLORS = [C.accent, C.orange, C.purple, C.pink, C.green, C.yellow];
function colorForCompound(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return COMPOUND_COLORS[Math.abs(hash) % COMPOUND_COLORS.length];
}

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function makeCalendarCells(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function isToday(year, month, day) {
  const t = new Date();
  return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
}

export default function MonthCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(now.getDate());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDose, setEditDose] = useState("");
  const [editTime, setEditTime] = useState("12:00");

  useEffect(() => {
    setLoading(true);
    const rangeStart = new Date(year, month - 1, 1).toISOString().slice(0, 10);
    const rangeEnd = new Date(year, month + 2, 0).toISOString().slice(0, 10);
    LogEntries.listForDateRange(rangeStart, rangeEnd)
      .then(setLogs)
      .catch(err => console.error('Failed to load logs for calendar:', err))
      .finally(() => setLoading(false));
  }, [year, month]);

  const logsByDate = useMemo(() => {
    const grouped = {};
    for (const log of logs) {
      const dateKey = String(log.date).slice(0, 10);
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(log);
    }
    return grouped;
  }, [logs]);

  const maxDosesInMonth = useMemo(() => {
    return Math.max(1, ...Object.values(logsByDate).map(l => l.length));
  }, [logsByDate]);

  const cells = useMemo(() => makeCalendarCells(year, month), [year, month]);
  const monthName = new Date(year, month, 1).toLocaleString("en-US", { month: "long" });

  const previousMonth = () => {
    const d = new Date(year, month - 1, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelectedDay(1);
  };

  const nextMonth = () => {
    const d = new Date(year, month + 1, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelectedDay(1);
  };

  const selectedKey = formatKey(year, month, selectedDay);
  const selectedLogs = selectedKey ? (logsByDate[selectedKey] || []) : [];

  useEffect(() => {
    if (editingId) {
      const log = selectedLogs.find(l => l.id === editingId);
      if (log) {
        setEditDose(String(log.dose || ""));
        const timeMatch = String(log.date).match(/T(\d{2}:\d{2})/);
        setEditTime(timeMatch ? timeMatch[1] : "12:00");
      }
    }
  }, [editingId]);

  const confirmDose = async (log) => {
    try {
      const originalDate = String(log.date).slice(0, 10);
      await LogEntries.update(log.id, {
        dose: parseFloat(editDose) || log.dose,
        date: new Date(`${originalDate}T${editTime}:00`).toISOString(),
        status: 'completed',
      });
      setLogs(prev => prev.map(l => l.id === log.id ? { ...l, dose: parseFloat(editDose) || l.dose, status: 'completed' } : l));
      setEditingId(null);
    } catch (err) {
      console.error('Failed to confirm dose:', err);
    }
  };

  return (
    <div style={{ fontFamily: C.sans, color: C.text, maxWidth: 960, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <button onClick={previousMonth} style={navBtnStyle}>←</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.muted, letterSpacing: "0.1em" }}>{year}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.text }}>{monthName}</div>
        </div>
        <button onClick={nextMonth} style={navBtnStyle}>→</button>
      </div>

      {/* Weekday row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 8 }}>
        {WEEKDAYS.map((d, i) => (
          <div key={d} style={{ fontSize: 10, letterSpacing: "0.06em", color: i === 0 ? C.red + "AA" : C.muted, textAlign: "center" }}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid — tactile cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 26, opacity: loading ? 0.5 : 1 }}>
        {cells.map((day, index) => {
          if (!day) return <div key={index} />;
          const key = formatKey(year, month, day);
          const dayLogs = logsByDate[key] || [];
          const selected = day === selectedDay;
          const today = isToday(year, month, day);
          const density = dayLogs.length / maxDosesInMonth;
          const hasPlanned = dayLogs.some(l => l.status === 'planned');
          const hasCompleted = dayLogs.some(l => l.status !== 'planned');

          return (
            <button
              key={index}
              onClick={() => setSelectedDay(day)}
              style={{
                background: selected
                  ? C.accent + "26"
                  : dayLogs.length > 0
                    ? `rgba(0, 212, 255, ${0.04 + density * 0.14})`
                    : C.surface,
                border: selected
                  ? `1.5px solid ${C.accent}`
                  : today
                    ? `1px solid ${C.accent}55`
                    : `1px solid ${C.border}`,
                borderRadius: 12,
                padding: "10px 4px 8px",
                minHeight: 88,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                fontFamily: "inherit",
                position: "relative",
                boxShadow: selected ? `0 2px 10px ${C.accent}22` : "none",
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: selected || today ? 700 : 500, color: selected || today ? C.accent : C.text }}>
                {day}
              </span>

              {dayLogs.length > 0 && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  minWidth: 22, height: 22, borderRadius: 11, padding: "0 6px",
                  background: hasPlanned && !hasCompleted ? "transparent" : C.accent + "cc",
                  border: hasPlanned && !hasCompleted ? `1.5px solid ${C.accent}` : "none",
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: C.mono, color: hasPlanned && !hasCompleted ? C.accent : C.bg }}>
                    {dayLogs.length}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Agenda for selected day */}
      <div>
        <div style={{ fontFamily: C.mono, fontSize: 11, color: C.muted, letterSpacing: "0.1em", marginBottom: 12 }}>
          {new Date(year, month, selectedDay).toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short" }).toUpperCase()}
        </div>

        {selectedLogs.length === 0 && (
          <div style={{ fontSize: 14, color: C.muted, padding: "24px 0", textAlign: "center", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
            No doses this day.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {selectedLogs.map(log => {
            const dotColor = colorForCompound(log.compound_name);
            const isEditing = editingId === log.id;

            return (
              <div key={log.id} style={{
                background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`,
                overflow: "hidden",
              }}>
                <div
                  onClick={() => log.status === 'planned' && setEditingId(isEditing ? null : log.id)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 14px", cursor: log.status === 'planned' ? "pointer" : "default",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{
                      width: 10, height: 10, borderRadius: "50%",
                      background: log.status === 'planned' ? "transparent" : dotColor,
                      border: `1.5px solid ${dotColor}`,
                    }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{log.compound_name}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{log.dose}{log.unit}</div>
                    </div>
                  </div>
                  {log.status === 'planned' && (
                    <span style={{
                      fontSize: 10, color: C.accent, fontFamily: C.mono, letterSpacing: "0.06em",
                      padding: "4px 9px", borderRadius: 6, border: `1px solid ${C.accent}44`,
                    }}>PLANNED</span>
                  )}
                </div>

                {isEditing && (
                  <div style={{ padding: "0 14px 14px", borderTop: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <input
                        type="number"
                        value={editDose}
                        onChange={e => setEditDose(e.target.value)}
                        style={editInputStyle}
                        placeholder="Dose"
                      />
                      <input
                        type="time"
                        value={editTime}
                        onChange={e => setEditTime(e.target.value)}
                        style={editInputStyle}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => setEditingId(null)} style={cancelBtnStyle}>Cancel</button>
                      <button onClick={() => confirmDose(log)} style={confirmBtnStyle}>Confirm Dose</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const navBtnStyle = {
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: C.muted,
  borderRadius: 8,
  width: 34,
  height: 34,
  fontSize: 15,
  cursor: "pointer",
};

const editInputStyle = {
  flex: 1,
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  color: C.text,
  padding: "8px 10px",
  fontSize: 13,
  fontFamily: "inherit",
};

const cancelBtnStyle = {
  flex: 1,
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: C.muted,
  borderRadius: 6,
  padding: "8px 0",
  fontSize: 12,
  cursor: "pointer",
};

const confirmBtnStyle = {
  flex: 2,
  background: C.accent,
  border: "none",
  color: C.bg,
  borderRadius: 6,
  padding: "8px 0",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};